// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';

// -------------------------------
// 🔹 Imports internes — Références réactives
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import { isAtcivatedWatchMangeOraganisation } from '@/refs/manageOrgCommerce.ref';

// -------------------------------
// 🔹 Imports internes — Store
// -------------------------------
import { useOrgAddressStore } from '@/store/orgAddress.store';

// -------------------------------
// 🔹 Imports internes — Types
// -------------------------------
import type {
  ICreateOrgAddressBody,
  IUpdateOrgAddressBody,
} from '@/types/manageOrgCommerce.types';

/**
 * Hook pour gérer les mutations liées aux adresses d'une organisation.
 */
export const OrgAddressMutation = () => {
  const orgAddressStore = useOrgAddressStore();

  // -------------------------------
  // 🔹 Gestion des erreurs globale
  // -------------------------------
  const handleError = (err: any) => {
    const error = err as AxiosError;
    const response = error.response?.data as any;
    const message = response?.message?.[0];

    isGlobalError.value = true;
    GlobalErrorMessage.value = message;
    isAtcivatedWatchMangeOraganisation.value =
      !isAtcivatedWatchMangeOraganisation.value;
    setTimeout(() => {
      isGlobalError.value = false;
    }, 1000);
  };

  // -------------------------------
  // 🔹 Création d'une adresse
  // -------------------------------
  const createOrgAddress = useMutation<
    any,
    Error,
    { organizationId: string; data: ICreateOrgAddressBody }
  >({
    mutationFn: ({ organizationId, data }) =>
      orgAddressStore.createOrgAddress(organizationId, data),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Mise à jour d'une adresse
  // -------------------------------
  const updateOrgAddress = useMutation<
    any,
    Error,
    { organizationId: string; addressId: string; data: IUpdateOrgAddressBody }
  >({
    mutationFn: ({ organizationId, addressId, data }) =>
      orgAddressStore.updateOrgAddress(organizationId, addressId, data),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Suppression d'une adresse
  // -------------------------------
  const deleteOrgAddress = useMutation<
    any,
    Error,
    { organizationId: string; addressId: string }
  >({
    mutationFn: ({ organizationId, addressId }) =>
      orgAddressStore.deleteOrgAddress(organizationId, addressId),
    onSuccess: () => {},
    onError: (err) => handleError(err),
  });

  // -------------------------------
  // 🔹 Fonctions exposées pour les composants
  // -------------------------------
  const handleCreateOrgAddress = (
    organizationId: string,
    data: ICreateOrgAddressBody,
  ) => createOrgAddress.mutateAsync({ organizationId, data });

  const handleUpdateOrgAddress = (
    organizationId: string,
    addressId: string,
    data: IUpdateOrgAddressBody,
  ) => updateOrgAddress.mutateAsync({ organizationId, addressId, data });

  const handleDeleteOrgAddress = (organizationId: string, addressId: string) =>
    deleteOrgAddress.mutateAsync({ organizationId, addressId });

  return {
    // mutations
    createOrgAddress,
    updateOrgAddress,
    deleteOrgAddress,
    // handlers
    handleCreateOrgAddress,
    handleUpdateOrgAddress,
    handleDeleteOrgAddress,
  };
};
